import React, { useState } from 'react';
import { User, CreditCard, Calendar, CheckCircle, AlertCircle, ChevronDown, ChevronUp, Phone, MapPin, Home, Clock } from 'lucide-react';

const UserInfoSidebar = ({ currentUser, selectedUHID, selectedAppointmentId, appointmentAction, selectedPatientDetails, selectedAppointmentDetails }) => {
  const [expandedSection, setExpandedSection] = useState(null);

  if (!currentUser) return null;

  const toggleSection = (section) => {
    setExpandedSection(expandedSection === section ? null : section);
  };

  return (
    <div className="bg-white border-l-4 border-blue-500 rounded-lg shadow-lg overflow-hidden">
      {/* Header */}
      <div className="bg-gradient-to-r from-blue-600 to-blue-700 text-white p-4">
        <h2 className="text-xl font-bold">Selection Panel</h2>
        <p className="text-blue-100 text-sm mt-1">View selected patient & appointment details</p>
      </div>

      {/* Content Area */}
      <div className="p-4 space-y-4 max-h-[calc(100vh-200px)] overflow-y-auto">

        {/* Selected UHID Section */}
        {selectedUHID && (
          <div className="border-2 border-green-300 rounded-lg overflow-hidden">
            <div
              className="bg-green-50 p-3 cursor-pointer hover:bg-green-100 transition-colors"
              onClick={() => toggleSection('uhid')}
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <CreditCard className="w-5 h-5 text-green-600" />
                  <div>
                    <p className="text-xs font-bold text-green-800 uppercase">Selected UHID</p>
                    <p className="text-lg font-mono font-bold text-green-700">{selectedUHID}</p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <CheckCircle className="w-5 h-5 text-green-600" />
                  {selectedPatientDetails && (
                    expandedSection === 'uhid' ? <ChevronUp className="w-5 h-5 text-green-600" /> : <ChevronDown className="w-5 h-5 text-green-600" />
                  )}
                </div>
              </div>
            </div>

            {/* Expanded Patient Details */}
            {expandedSection === 'uhid' && selectedPatientDetails && (
              <div className="bg-white p-4 border-t-2 border-green-200 space-y-3">
                <h4 className="text-sm font-bold text-gray-700 uppercase border-b pb-2">Patient Information</h4>

                <div className="space-y-2">
                  <div className="flex items-start gap-2">
                    <User className="w-4 h-4 text-gray-400 mt-1" />
                    <div className="flex-1">
                      <p className="text-xs text-gray-500 uppercase">Full Name</p>
                      <p className="text-sm font-semibold text-gray-800">{selectedPatientDetails.fullName}</p>
                    </div>
                  </div>

                  <div className="flex items-start gap-2">
                    <Phone className="w-4 h-4 text-gray-400 mt-1" />
                    <div className="flex-1">
                      <p className="text-xs text-gray-500 uppercase">Phone Number</p>
                      <p className="text-sm font-semibold text-gray-800">{selectedPatientDetails.phoneNumber}</p>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <p className="text-xs text-gray-500 uppercase">Age</p>
                      <p className="text-sm font-semibold text-gray-800">{selectedPatientDetails.age}</p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-500 uppercase">Gender</p>
                      <p className="text-sm font-semibold text-gray-800 capitalize">{selectedPatientDetails.gender}</p>
                    </div>
                  </div>

                  <div className="flex items-start gap-2">
                    <MapPin className="w-4 h-4 text-gray-400 mt-1" />
                    <div className="flex-1">
                      <p className="text-xs text-gray-500 uppercase">City</p>
                      <p className="text-sm font-semibold text-gray-800">{selectedPatientDetails.city}</p>
                    </div>
                  </div>

                  <div className="flex items-start gap-2">
                    <MapPin className="w-4 h-4 text-gray-400 mt-1" />
                    <div className="flex-1">
                      <p className="text-xs text-gray-500 uppercase">Pincode</p>
                      <p className="text-sm font-semibold text-gray-800">{selectedPatientDetails.pincode}</p>
                    </div>
                  </div>

                  <div className="flex items-start gap-2">
                    <Home className="w-4 h-4 text-gray-400 mt-1" />
                    <div className="flex-1">
                      <p className="text-xs text-gray-500 uppercase">Full Address</p>
                      <p className="text-sm text-gray-800">{selectedPatientDetails.fullAddress}</p>
                    </div>
                  </div>

                  {selectedPatientDetails.registrationDate && (
                    <div className="flex items-start gap-2">
                      <Calendar className="w-4 h-4 text-gray-400 mt-1" />
                      <div className="flex-1">
                        <p className="text-xs text-gray-500 uppercase">Registration Date</p>
                        <p className="text-sm text-gray-800">
                          {new Date(selectedPatientDetails.registrationDate).toLocaleDateString('en-US', {
                            year: 'numeric',
                            month: 'long',
                            day: 'numeric'
                          })}
                        </p>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Selected Appointment Section */}
        {selectedAppointmentId && (
          <div className="border-2 border-purple-300 rounded-lg overflow-hidden">
            <div
              className="bg-purple-50 p-3 cursor-pointer hover:bg-purple-100 transition-colors"
              onClick={() => toggleSection('appointment')}
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Calendar className="w-5 h-5 text-purple-600" />
                  <div>
                    <p className="text-xs font-bold text-purple-800 uppercase">Selected Appointment</p>
                    <p className="text-lg font-mono font-bold text-purple-700">{selectedAppointmentId}</p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <CheckCircle className="w-5 h-5 text-purple-600" />
                  {selectedAppointmentDetails && (
                    expandedSection === 'appointment' ? <ChevronUp className="w-5 h-5 text-purple-600" /> : <ChevronDown className="w-5 h-5 text-purple-600" />
                  )}
                </div>
              </div>

              {appointmentAction && (
                <div className={`mt-2 pt-2 border-t ${
                  appointmentAction === 'reschedule' ? 'border-orange-300' : 'border-red-300'
                }`}>
                  <p className="text-xs font-semibold uppercase text-gray-600">Action</p>
                  <p className={`text-sm font-bold ${
                    appointmentAction === 'reschedule' ? 'text-orange-600' : 'text-red-600'
                  }`}>
                    {appointmentAction === 'reschedule' ? 'RESCHEDULE' : 'CANCEL'}
                  </p>
                </div>
              )}
            </div>

            {/* Expanded Appointment Details */}
            {expandedSection === 'appointment' && selectedAppointmentDetails && (
              <div className="bg-white p-4 border-t-2 border-purple-200 space-y-3">
                <h4 className="text-sm font-bold text-gray-700 uppercase border-b pb-2">Appointment Information</h4>

                <div className="space-y-2">
                  <div className="flex items-start gap-2">
                    <CreditCard className="w-4 h-4 text-gray-400 mt-1" />
                    <div className="flex-1">
                      <p className="text-xs text-gray-500 uppercase">Patient UHID</p>
                      <p className="text-sm font-mono font-bold text-gray-800">{selectedAppointmentDetails.uhid}</p>
                    </div>
                  </div>

                  <div className="flex items-start gap-2">
                    <MapPin className="w-4 h-4 text-gray-400 mt-1" />
                    <div className="flex-1">
                      <p className="text-xs text-gray-500 uppercase">Hospital</p>
                      <p className="text-sm font-semibold text-gray-800">{selectedAppointmentDetails.hospital}</p>
                    </div>
                  </div>

                  <div className="flex items-start gap-2">
                    <User className="w-4 h-4 text-gray-400 mt-1" />
                    <div className="flex-1">
                      <p className="text-xs text-gray-500 uppercase">Doctor</p>
                      <p className="text-sm font-semibold text-gray-800">{selectedAppointmentDetails.doctor}</p>
                    </div>
                  </div>

                  <div className="flex items-start gap-2">
                    <Calendar className="w-4 h-4 text-gray-400 mt-1" />
                    <div className="flex-1">
                      <p className="text-xs text-gray-500 uppercase">Appointment Date</p>
                      <p className="text-sm font-semibold text-gray-800">{selectedAppointmentDetails.date}</p>
                    </div>
                  </div>

                  <div className="flex items-start gap-2">
                    <Clock className="w-4 h-4 text-gray-400 mt-1" />
                    <div className="flex-1">
                      <p className="text-xs text-gray-500 uppercase">Time Slot</p>
                      <p className="text-sm font-semibold text-gray-800">{selectedAppointmentDetails.slot}</p>
                    </div>
                  </div>

                  <div className="flex items-start gap-2">
                    <div className={`w-4 h-4 rounded-full mt-1 ${
                      selectedAppointmentDetails.status === 'confirmed' ? 'bg-green-500' :
                      selectedAppointmentDetails.status === 'rescheduled' ? 'bg-yellow-500' :
                      'bg-red-500'
                    }`} />
                    <div className="flex-1">
                      <p className="text-xs text-gray-500 uppercase">Status</p>
                      <p className="text-sm font-semibold text-gray-800 capitalize">{selectedAppointmentDetails.status}</p>
                    </div>
                  </div>

                  {selectedAppointmentDetails.createdAt && (
                    <div className="flex items-start gap-2">
                      <Calendar className="w-4 h-4 text-gray-400 mt-1" />
                      <div className="flex-1">
                        <p className="text-xs text-gray-500 uppercase">Booked On</p>
                        <p className="text-sm text-gray-800">
                          {new Date(selectedAppointmentDetails.createdAt).toLocaleString('en-US', {
                            year: 'numeric',
                            month: 'long',
                            day: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit'
                          })}
                        </p>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Data Access Info */}
        <div className={`rounded-lg p-3 ${
          currentUser.is_admin
            ? 'bg-red-50 border-2 border-red-200'
            : 'bg-blue-50 border-2 border-blue-200'
        }`}>
          <div className="flex items-start gap-2">
            <AlertCircle className={`w-5 h-5 flex-shrink-0 mt-0.5 ${
              currentUser.is_admin ? 'text-red-600' : 'text-blue-600'
            }`} />
            <div>
              <p className={`text-sm font-bold mb-1 ${
                currentUser.is_admin ? 'text-red-800' : 'text-blue-800'
              }`}>
                {currentUser.is_admin ? 'Admin Access' : 'User Access'}
              </p>
              <p className={`text-xs ${
                currentUser.is_admin ? 'text-red-700' : 'text-blue-700'
              }`}>
                {currentUser.is_admin
                  ? 'You can view and manage all users and appointments'
                  : 'You can only view and manage your own data'}
              </p>
            </div>
          </div>
        </div>

        {/* Instructions - Always visible when nothing is selected */}
        {!selectedUHID && !selectedAppointmentId && (
          <div className="bg-gray-50 border-2 border-gray-200 rounded-lg p-3">
            <p className="text-sm font-semibold text-gray-700 mb-2">Quick Guide:</p>
            <ul className="text-xs text-gray-600 space-y-1">
              <li>• Click UHID cards to view patient details</li>
              <li>• Click appointment cards to view details</li>
              <li>• Selected IDs will appear above</li>
              <li>• Click sections to expand/collapse details</li>
            </ul>
          </div>
        )}
      </div>
    </div>
  );
};

export default UserInfoSidebar;